/* ─────────────────────────────────────────────────────────────────────────
   Delivery speaks IST — one clock for the whole dashboard.

   Ops runs on the IST calendar day. The server says so (`istDayBounds`:
   "'today'/'overdue' are IST days, matching the reminder sweep's convention"),
   and the urgency lens, triage rail and daily sweep all obey it — so the client
   must answer "what day is this card due?" the same way, or one screen carries
   three different answers (a card due 20 Jul 03:00 IST would render on the
   19th, count as overdue, and read "Due today" all at once).

   Everything here is offset arithmetic against a fixed +05:30 rather than
   `Intl` with a named zone: India has no DST, the server's own helper is the
   same arithmetic, and `toLocaleDateString({ timeZone: "Asia/Kolkata" })`
   needs an ICU build we can't assume in every test runner. `timeZone: "UTC"`
   applied to an already-shifted instant is exact and always available. */

export const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
export const IST_UTC_OFFSET = "+05:30";
export const DAY_MS = 24 * 60 * 60 * 1000;

/** The same instant, shifted so that reading its UTC fields yields IST wall
 *  time. Internal — callers want one of the named helpers below. */
function istFields(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getTime() + IST_OFFSET_MS);
}

/** The instant at which an IST calendar day begins (00:00 IST). */
export function istStartOfDay(value) {
  const ist = istFields(value);
  if (!ist) return null;
  return new Date(
    Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()) -
      IST_OFFSET_MS
  );
}

/** Whole IST calendar days from `from` to `to` (negative = in the past).
 *  Both operands are exact IST midnights, so the division is exact — no
 *  rounding error to paper over. */
export function istDayDiff(to, from) {
  const a = istStartOfDay(to);
  const b = istStartOfDay(from);
  if (!a || !b) return null;
  return Math.round((a.getTime() - b.getTime()) / DAY_MS);
}

/** "YYYY-MM-DD" for the IST calendar day an instant falls in — the key the
 *  server's `$dateToString` produces with `timezone: "+05:30"`. */
export function istDateKey(value) {
  const ist = istFields(value);
  if (!ist) return null;
  const m = String(ist.getUTCMonth() + 1).padStart(2, "0");
  const d = String(ist.getUTCDate()).padStart(2, "0");
  return `${ist.getUTCFullYear()}-${m}-${d}`;
}

/** Today's IST calendar parts — what the grid means by "today" and what the
 *  month navigation starts on. */
export function istToday(now = new Date()) {
  const ist = istFields(now);
  return {
    year: ist.getUTCFullYear(),
    month: ist.getUTCMonth() + 1, // 1-12
    day: ist.getUTCDate(),
  };
}

/** Calendar-grid facts for an IST month: which weekday the 1st falls on and
 *  how many days it has. Derived from UTC fields of a shifted date so the
 *  browser's own zone can never rotate the grid. */
export function istMonthGrid(year, month) {
  const firstDow = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { firstDow, daysInMonth };
}

/** The half-open instants bounding one IST calendar day, as ISO-8601 strings
 *  carrying the +05:30 offset explicitly — this is what the day query sends,
 *  so the server returns the IST day whose cell was clicked, not a UTC day. */
export function istDayRangeIso(dateKey) {
  return {
    from: `${dateKey}T00:00:00.000${IST_UTC_OFFSET}`,
    to: `${dateKey}T23:59:59.999${IST_UTC_OFFSET}`,
  };
}

/** Format an instant on the IST calendar. `timeZone: "UTC"` over an
 *  already-shifted instant is exact and needs no ICU timezone database. */
export function formatIstDate(value, options) {
  const ist = istFields(value);
  if (!ist) return "";
  return ist.toLocaleDateString("en-IN", { ...options, timeZone: "UTC" });
}
